import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateBlogPostSchema, generateBreadcrumbSchema } from '@/components/SEO';
import { getBlogBySlug } from '@/lib/api';
import { format } from 'date-fns';
import { ArrowLeft, Calendar, User, Tag, ArrowUpRight, Loader2 } from 'lucide-react';
import { trackBlogView, trackBookCall } from '@/lib/gtm';

export default function BlogPost() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { loadBlog(); /* eslint-disable-next-line */ }, [slug]);

  const loadBlog = async () => {
    setLoading(true);
    try {
      const res = await getBlogBySlug(slug);
      setBlog(res.data);
      trackBlogView(res.data.slug, res.data.title);
    } catch (err) {
      setError('Blog post not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#FAFAF7' }}>
        <Navbar />
        <div className="pt-40 pb-24 flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen" style={{ background: '#FAFAF7' }}>
        <Navbar />
        <div className="pt-32 pb-24 flex flex-col items-center justify-center min-h-[60vh]">
          <h1 className="font-syne text-3xl font-bold text-[#0A0A0A] mb-4">Post not found</h1>
          <p className="text-[#5B5B5B] mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-[#ff5a1f] font-semibold flex items-center gap-2 hover:gap-3 transition-all">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Approximate read time
  const wordCount = (blog.content || '').split(/\s+/).length;
  const readMins = Math.max(1, Math.round(wordCount / 220));

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid="blog-post-page">
      <SEO
        title={blog.title}
        description={blog.excerpt}
        keywords={blog.tags?.join(', ')}
        url={`/blog/${blog.slug}`}
        image={blog.image}
        type="article"
        article={{
          publishedTime: blog.created_at,
          modifiedTime: blog.updated_at || blog.created_at,
          author: blog.author,
          tags: blog.tags,
        }}
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            generateBlogPostSchema({
              title: blog.title,
              excerpt: blog.excerpt,
              image: blog.image,
              slug: blog.slug,
              author: blog.author,
              publishedDate: blog.created_at,
              modifiedDate: blog.updated_at || blog.created_at,
            }),
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Blog', path: '/blog' },
              { name: blog.title, path: `/blog/${blog.slug}` },
            ]),
          ],
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-10 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-50" />
        <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
          <Link to="/blog" className="text-[#5B5B5B] text-sm flex items-center gap-2 hover:text-[#ff5a1f] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>

          <div className="flex flex-wrap gap-2 mb-6">
            {blog.tags?.map((tag) => (
              <Link key={tag} to={`/blog?tag=${tag}`} className="text-xs font-mono text-[#ff5a1f] uppercase flex items-center gap-1 hover:underline">
                <Tag className="w-3 h-3" />{tag}
              </Link>
            ))}
          </div>

          <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A0A0A] mb-8 tracking-tight leading-[1.05]">
            {blog.title}
          </h1>

          <div className="flex items-center gap-6 text-[#5B5B5B] text-sm pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
            <span className="flex items-center gap-2"><User className="w-4 h-4 text-[#ff5a1f]" />{blog.author}</span>
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#ff5a1f]" />
              {blog.created_at && format(new Date(blog.created_at), 'MMMM d, yyyy')}
            </span>
            <span>{readMins} min read</span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {blog.image && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="aspect-[16/9] overflow-hidden rounded-3xl bg-[#F4F2EC]">
              <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="pb-24 bg-white" data-testid="blog-content">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-16">
          <div className="prose prose-lg max-w-none">
            <div className="text-[#2A2A2A] leading-relaxed space-y-6"
              dangerouslySetInnerHTML={{ __html: (blog.content || '').replace(/\n/g, '<br/>') }} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FAFAF7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-3xl md:text-5xl font-bold text-[#0A0A0A] mb-6 tracking-tight">
            Want this kind of thinking on <span className="font-editorial text-[#ff5a1f]">your brief?</span>
          </h2>
          <p className="text-[#5B5B5B] text-lg mb-10">Book a free 40-minute call with our team.</p>
          <Link to="/book-appointment" onClick={() => trackBookCall(`blog_${blog.slug}`)}
            className="bg-[#0A0A0A] text-white px-10 py-5 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press inline-flex items-center gap-2">
            Book a call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
